import { useLazyQuery } from '@apollo/client'
import { Card, CardContent, Skeleton, Stack, Typography } from '@mui/material'
import { isEmpty } from 'lodash'
import React, { useEffect } from 'react'

import { useRefresh } from '@/contexts/RefreshContext'
import {
  ActionType,
  GET_EMPLOYEE_ACTION_COMPLETION,
  GetEmployeeActionCompletionQuery,
  GetEmployeeActionCompletionQueryVariables,
  SortDirection,
} from '@/graphql'
import useT from '@/i18n/useT'

import { UserAvatarWithQuery } from '../users/UserAvatarWithQuery'

type Props = {
  startTime?: string
  endTime?: string
  storeIds?: string[]
  rankEnd: 'top' | 'bottom'
  actionTypes?: ActionType[]
  isRestock?: boolean
  actionShiftIds?: string[]
}

export const RankedEmployeeStat: React.FC<Props> = props => {
  const { t } = useT()
  const {
    startTime,
    endTime,
    storeIds,
    rankEnd,
    actionTypes,
    isRestock,
    actionShiftIds,
  } = props

  const [
    getEmployeeActionCompletion,
    { data: employeeActionCompletionData, loading, refetch, variables },
  ] = useLazyQuery<
    GetEmployeeActionCompletionQuery,
    GetEmployeeActionCompletionQueryVariables
  >(GET_EMPLOYEE_ACTION_COMPLETION, { notifyOnNetworkStatusChange: true })

  useRefresh(() => {
    if (!isEmpty(variables)) {
      refetch()
    }
  })

  const employees = employeeActionCompletionData?.employeeActionCompletion
  const highlightedEmployees = employees?.slice(0, 3) || []

  useEffect(() => {
    if (startTime && endTime) {
      getEmployeeActionCompletion({
        variables: {
          filters: {
            startTime,
            endTime,
            storeIds,
            actionTypes,
            isRestock,
            actionShiftIds,
          },
          order: rankEnd === 'top' ? SortDirection.DESC : SortDirection.ASC,
        },
      })
    }
  }, [
    startTime,
    endTime,
    isRestock,
    JSON.stringify(storeIds),
    JSON.stringify(actionTypes),
    JSON.stringify(actionShiftIds),
  ])

  return (
    <Card sx={{ flex: 1 }}>
      <CardContent>
        {loading ? (
          <>
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </>
        ) : (
          <>
            <Typography variant="h6" mb={4}>
              {rankEnd === 'top'
                ? t('rankedEmployeeStat.topTitle')
                : t('rankedEmployeeStat.bottomTitle')}
            </Typography>
            <Stack direction={'column'} spacing={1}>
              {highlightedEmployees.length > 0 ? (
                highlightedEmployees.map(employee => (
                  <Stack
                    key={employee.user.id}
                    direction={'row'}
                    spacing={4}
                    sx={{ alignItems: 'center' }}
                  >
                    <Typography variant="subtitle1">{employee.rank}</Typography>
                    <Stack
                      direction={'row'}
                      spacing={1}
                      sx={{ alignItems: 'center' }}
                    >
                      <UserAvatarWithQuery id={employee.user.id} includeName />
                      <Typography key={employee.user.id} variant="body2">
                        - {employee.numberOfCompletions}x
                      </Typography>
                    </Stack>
                  </Stack>
                ))
              ) : (
                <Typography variant="body2">
                  {t('rankedEmployeeStat.noEmployees')}
                </Typography>
              )}
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  )
}
